import { CheckCircle2, Circle, FileText, CalendarRange, Clock } from 'lucide-react'
import type { GestorEquipeDetail } from '@/services/gestoresService'
import { formatPtDate } from '@/components/Gestores/gestorEquipeFeriasUtils'
import { cn } from '@/utils/cn'

type Resumo = NonNullable<GestorEquipeDetail['feriasResumo']>

type Props = {
  detail: GestorEquipeDetail
  resumo: Resumo
}

const NF_MINIMA = 12
const MESES_MINIMOS = 12
const DIAS_TOTAIS = 20

function ProgressBar({
  value,
  max,
  tone = 'accent',
}: {
  value: number
  max: number
  tone?: 'accent' | 'ok' | 'warn'
}) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0
  return (
    <div className='mt-2 h-1.5 overflow-hidden rounded-full bg-white/[0.08]'>
      <div
        className={cn(
          'h-full rounded-full transition-all',
          tone === 'ok' && 'bg-emerald-500',
          tone === 'warn' && 'bg-amber-500',
          tone === 'accent' && 'bg-accent',
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

function CriterionRow({
  ok,
  icon,
  title,
  detail,
  progress,
}: {
  ok: boolean
  icon: React.ReactNode
  title: string
  detail: string
  progress?: { value: number; max: number; tone: 'ok' | 'warn' | 'accent' }
}) {
  return (
    <div
      className={cn(
        'flex gap-3 rounded-lg border px-3 py-3',
        ok ? 'border-emerald-500/20 bg-emerald-500/[0.04]' : 'border-white/[0.06] bg-white/[0.02]',
      )}
    >
      <div className='mt-0.5 shrink-0'>
        {ok ? (
          <CheckCircle2 size={18} className='text-emerald-500' />
        ) : (
          <Circle size={18} className='text-text-muted' />
        )}
      </div>
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='text-text-muted'>{icon}</span>
          <p className='text-sm font-medium text-text-primary'>{title}</p>
        </div>
        <p className='mt-0.5 text-xs text-text-muted'>{detail}</p>
        {progress && (
          <ProgressBar value={progress.value} max={progress.max} tone={progress.tone} />
        )}
      </div>
    </div>
  )
}

function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className='rounded-lg border border-white/[0.06] bg-white/[0.03] px-4 py-3'>
      <p className='text-[11px] font-medium text-text-muted'>{label}</p>
      <p className='mt-1 text-xl font-semibold tabular-nums text-text-primary'>{value}</p>
      {sub && <p className='mt-0.5 text-xs text-text-muted'>{sub}</p>}
    </div>
  )
}

export function GestorEquipeFeriasEligibility({ detail, resumo }: Props) {
  const diasUsadosPct = Math.round((resumo.diasUsados / DIAS_TOTAIS) * 100)

  return (
    <div className='space-y-4'>
      <div
        className={cn(
          'flex flex-wrap items-start justify-between gap-3 rounded-xl border px-4 py-4 sm:px-5',
          resumo.podeSolicitarFerias
            ? 'border-emerald-500/25 bg-emerald-500/[0.06]'
            : 'border-amber-500/20 bg-amber-500/[0.05]',
        )}
      >
        <div>
          <p className='text-base font-semibold text-text-primary'>Elegibilidade para férias PJ</p>
          <p className='mt-1 max-w-xl text-sm text-text-muted'>
            {resumo.podeSolicitarFerias
              ? 'Profissional atende aos critérios e pode solicitar descanso fracionado (até 20 dias).'
              : resumo.motivoBloqueio ?? 'Ainda não elegível para férias.'}
          </p>
        </div>
        <span
          className={cn(
            'rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset',
            resumo.podeSolicitarFerias
              ? 'bg-emerald-500/15 text-emerald-400 ring-emerald-500/30'
              : 'bg-amber-500/15 text-amber-400 ring-amber-500/30',
          )}
        >
          {resumo.podeSolicitarFerias ? 'Elegível' : 'Pendente'}
        </span>
      </div>

      <div className='grid gap-3 lg:grid-cols-2'>
        <div className='space-y-2'>
          <p className='text-xs font-semibold uppercase tracking-wide text-text-muted'>Critérios</p>
          <CriterionRow
            ok={resumo.elegivelPorTempo}
            icon={<Clock size={14} />}
            title='Tempo de parceria'
            detail={`${resumo.mesesParceria} de ${MESES_MINIMOS} meses · início ${formatPtDate(resumo.dataInicioContrato ?? detail.dataInicioContrato)}`}
            progress={{
              value: Math.min(resumo.mesesParceria, MESES_MINIMOS),
              max: MESES_MINIMOS,
              tone: resumo.elegivelPorTempo ? 'ok' : 'warn',
            }}
          />
          <CriterionRow
            ok={resumo.elegivelPorNf}
            icon={<FileText size={14} />}
            title='Notas fiscais emitidas'
            detail={`${resumo.nfsEmitidas} de ${NF_MINIMA} NFs aprovadas/enviadas`}
            progress={{
              value: resumo.nfsEmitidas,
              max: NF_MINIMA,
              tone: resumo.elegivelPorNf ? 'ok' : 'warn',
            }}
          />
          <CriterionRow
            ok={Boolean(resumo.elegivelDesde)}
            icon={<CalendarRange size={14} />}
            title='Elegível desde'
            detail={
              resumo.elegivelDesde
                ? formatPtDate(resumo.elegivelDesde)
                : 'Calculado quando tempo e NF forem atendidos'
            }
          />
        </div>

        <div className='space-y-3'>
          <p className='text-xs font-semibold uppercase tracking-wide text-text-muted'>Saldo</p>
          <div className='rounded-xl border border-white/[0.06] bg-white/[0.03] p-4'>
            <div className='flex items-end justify-between gap-4'>
              <div>
                <p className='text-3xl font-bold tabular-nums text-text-primary'>
                  {resumo.diasRestantes}
                  <span className='ml-1 text-base font-normal text-text-muted'>/ {DIAS_TOTAIS} dias</span>
                </p>
                <p className='mt-1 text-sm text-text-muted'>disponíveis para descanso</p>
              </div>
              <div className='text-right text-xs text-text-muted'>
                <p>{resumo.diasUsados} utilizados</p>
              </div>
            </div>
            <ProgressBar
              value={resumo.diasUsados}
              max={DIAS_TOTAIS}
              tone={resumo.diasRestantes > 0 ? 'accent' : 'warn'}
            />
            <p className='mt-2 text-[11px] text-text-muted'>{diasUsadosPct}% do saldo consumido</p>
          </div>

          <div className='grid grid-cols-2 gap-2'>
            <StatTile label='Parceria' value={`${resumo.mesesParceria}m`} sub='desde admissão' />
            <StatTile label='NFs' value={String(resumo.nfsEmitidas)} sub={`meta ${NF_MINIMA}`} />
          </div>
        </div>
      </div>
    </div>
  )
}
